'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { GatedButton } from '@/src/components/ui/GatedButton';
import {
  Box,
  Button,
  Checkbox,
  FormField,
  FormHelp,
  Input,
  Label,
  LabeledControl,
  Select,
  Slider,
  Text,
} from '@zillow/constellation-10';
import BedroomsRangeToggleGroup from '@/src/components/ListingSearch/BedroomsRangeToggleGroup';
import { SHARE_CLICKSTREAM_CONTEXTS } from '@/src/components/features/listing-actions/listingActionUtils';
import { ListingCard } from '@/src/components/ListingCard';
import { CAROUSEL_CARD_IMAGE_SIZES, ListingCarousel } from '@/src/components/ListingCarousel';
import type { SimilarListing } from '@/src/components/features/property/hooks/useSimilarListings';
import { useFeatureFlag } from '@/src/lib/feature-flags';
import {
  emitSearchFilterChangeEvents,
  POST_CONTACT_FILTER_TRACKING,
} from '@/src/components/ListingSearch/filterAnalytics';
import { DEFAULT_FILTERS, type FilterState } from '@/src/lib/filters';
import { useCreateInquiry, useCreateSavedSearch, useCurrentUser } from '@/src/lib/hooks/query';
import { buildSavedSearchPayload, type SavedSearchFrequency } from '@/src/lib/savedSearches';
import {
  getSavedSearchNameError,
  SAVED_SEARCH_FREQUENCY_LABEL_KEYS,
  SAVED_SEARCH_FREQUENCY_OPTIONS,
} from '@/src/components/ListingSearch/saveSearchFormUtils';
import {
  getPostContactListingId,
  getPostContactListingTitle,
  toPostContactListingCardListing,
} from './postContactListingCardAdapter';
import type { ContactFormSavedSearchContext, ContactSubmissionContext } from './types';
import {
  emitTrackCreateSearchAlertEvent,
  emitTrackPostContactPopupViewEvent,
  emitTrackSimilarListingMultiContactMessageButtonClickEvent,
  emitTrackSimilarListingMultiContactSubmitEvent,
} from '@/src/analytics/hotpads/events';
import { toHotpadsClickstreamListing } from '@/src/analytics/hotpads/track';
import { analytics } from '@/src/components/features/property/lib';
import {
  getContactFormClickstreamContext,
  getPostContactClickstreamContext,
} from '@/src/components/features/property/lib/analytics';
import * as styles from './postContactFlow.styles';

export type PostContactFlowStepProps = {
  context: ContactSubmissionContext;
  onComplete: () => void;
};

const PRICE_MIN = 0;
const PRICE_MAX = 10000;
const PRICE_STEP = 100;

function createRentalSubmitId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function emitSuccessfulSimilarListingInquiryEvents(
  context: ContactSubmissionContext,
  listing: SimilarListing,
  rentalSubmitId: string,
) {
  const clickstreamListing = toHotpadsClickstreamListing(listing);
  const leadClickstreamContext = getContactFormClickstreamContext(
    context.clickstreamContext,
    'submit',
    context.formMode,
  );
  const postContactClickstreamContext = getPostContactClickstreamContext(context.clickstreamContext);

  analytics.emitTrackEmailLeadEvent({
    listing: clickstreamListing,
    ...leadClickstreamContext,
    triggerSource: 'button_to_send_message',
  });
  emitTrackSimilarListingMultiContactSubmitEvent({
    listing: clickstreamListing,
    rentalSubmitId,
    ...postContactClickstreamContext,
  });
}

function formatInputValue(num: number | null): string {
  return num === null ? '' : String(num);
}

function parseInputValue(raw: string): number | null {
  const digits = raw.replace(/\D/g, '');
  return digits === '' ? null : parseInt(digits, 10);
}

export function hasDefaultSavedSearchTuningFilters(filters: FilterState) {
  return (
    filters.price.min === DEFAULT_FILTERS.price.min &&
    filters.price.max === DEFAULT_FILTERS.price.max &&
    filters.bedrooms.min === DEFAULT_FILTERS.bedrooms.min &&
    filters.bedrooms.max === DEFAULT_FILTERS.bedrooms.max
  );
}

function PostContactListingCard({
  disabled = false,
  isSelected,
  listing,
  onSelectedChange,
}: {
  disabled?: boolean;
  isSelected?: boolean;
  listing: SimilarListing;
  onSelectedChange?: (listingId: string, checked: boolean) => void;
}) {
  const t = useTranslations('contactForm');
  const listingId = getPostContactListingId(listing);
  const listingTitle = getPostContactListingTitle(listing);
  const isSelectable = Boolean(onSelectedChange);

  return (
    <div className={styles.listingCardShell} data-testid={`post-contact-card-${listingId}`}>
      {isSelectable && (
        <LabeledControl
          label={<Label>{t('postContactSelectCard')}</Label>}
          control={
            <Checkbox
              checked={Boolean(isSelected)}
              onChange={(event) => onSelectedChange?.(listingId, event.target.checked)}
              disabled={disabled}
              aria-label={t('postContactSelectListing', { title: listingTitle })}
            />
          }
        />
      )}
      <ListingCard
        clickstreamTriggerLocation={'similar_to_contacted_collection'}
        actionClickstreamContext={SHARE_CLICKSTREAM_CONTEXTS.postContact}
        listing={toPostContactListingCardListing(listing)}
        appearance="small"
        sizes={CAROUSEL_CARD_IMAGE_SIZES}
        hideListingActions
      />
    </div>
  );
}

function SavedSearchFilterTuning({
  filters,
  onChange,
}: {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
}) {
  const tContact = useTranslations('contactForm');
  const tFilters = useTranslations('filters');
  const tSearch = useTranslations('search');
  const sliderValue: [number, number] = [filters.price.min ?? PRICE_MIN, filters.price.max ?? PRICE_MAX];

  const updatePrice = (price: FilterState['price']) => {
    const nextFilters = { ...filters, price };
    emitSearchFilterChangeEvents(filters, nextFilters, POST_CONTACT_FILTER_TRACKING);
    onChange(nextFilters);
  };

  const updateBedrooms = (bedrooms: FilterState['bedrooms']) => {
    const nextFilters = { ...filters, bedrooms };
    emitSearchFilterChangeEvents(filters, nextFilters, POST_CONTACT_FILTER_TRACKING);
    onChange(nextFilters);
  };

  const handleSliderChange = (next: Array<number>) => {
    const [lo, hi] = next;
    updatePrice({
      min: lo === PRICE_MIN ? null : lo,
      max: hi === PRICE_MAX ? null : hi,
    });
  };

  return (
    <div className={styles.filterTuning}>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
        <Text asChild textStyle="body-sm-bold" color="text.subtle">
          <p>{tContact('postContactSavedSearchPriceLabel')}</p>
        </Text>
        <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
          <Input
            type="text"
            inputMode="numeric"
            placeholder={tFilters('noMin')}
            aria-label={tSearch('minimumPrice')}
            value={formatInputValue(filters.price.min)}
            onChange={(event) => updatePrice({ ...filters.price, min: parseInputValue(event.target.value) })}
            size="sm"
          />
          <Text textStyle="body-sm" color="text.subtle">
            {tFilters('to')}
          </Text>
          <Input
            type="text"
            inputMode="numeric"
            placeholder={tFilters('noMax')}
            aria-label={tSearch('maximumPrice')}
            value={formatInputValue(filters.price.max)}
            onChange={(event) => updatePrice({ ...filters.price, max: parseInputValue(event.target.value) })}
            size="sm"
          />
        </Box>
        <div className={styles.rangeSliderInset}>
          <Slider
            min={PRICE_MIN}
            max={PRICE_MAX}
            step={PRICE_STEP}
            value={sliderValue}
            onValueChange={handleSliderChange}
            ariaLabel={({ index }) => (index === 0 ? tSearch('minimumPrice') : tSearch('maximumPrice'))}
            ariaValuetext={({ valueNow }) => `$${valueNow.toLocaleString('en-US')}`}
          />
        </div>
      </Box>

      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
        <Text asChild textStyle="body-sm-bold" color="text.subtle">
          <p>{tContact('postContactSavedSearchBedroomsLabel')}</p>
        </Text>
        <BedroomsRangeToggleGroup bedrooms={filters.bedrooms} onChange={updateBedrooms} />
      </Box>
    </div>
  );
}

function SavedSearchStep({
  savedSearchContext,
  onComplete,
}: {
  savedSearchContext: ContactFormSavedSearchContext;
  onComplete: () => void;
}) {
  const t = useTranslations('contactForm');
  const saveSearchT = useTranslations('saveSearchModal');
  const { data: currentUser } = useCurrentUser();
  const createSavedSearch = useCreateSavedSearch();
  const hasResolvedIdentity = currentUser !== undefined;
  const shouldShowFilterTuning = hasDefaultSavedSearchTuningFilters(savedSearchContext.filters);
  const [name, setName] = useState(savedSearchContext.locationText);
  const [frequency, setFrequency] = useState<SavedSearchFrequency>('instant');
  const [draftFilters, setDraftFilters] = useState(savedSearchContext.filters);
  const [nameError, setNameError] = useState<string | undefined>();
  const lastTrackedOffer = useRef<ContactFormSavedSearchContext | null>(null);
  // v3 envelope messages are not renter copy.
  const mutationError = createSavedSearch.error ? saveSearchT('errorGeneric') : '';

  useEffect(() => {
    if (lastTrackedOffer.current !== savedSearchContext) {
      lastTrackedOffer.current = savedSearchContext;
      emitTrackPostContactPopupViewEvent();
    }
    setName(savedSearchContext.locationText);
    setFrequency('instant');
    setDraftFilters(savedSearchContext.filters);
    setNameError(undefined);
  }, [savedSearchContext]);

  const handleSubmit = () => {
    if (!hasResolvedIdentity) return;

    const trimmedName = name.trim();
    const nextNameError = getSavedSearchNameError(trimmedName, saveSearchT);

    setNameError(nextNameError);

    if (nextNameError) return;

    // No recipient: the v3 inquiry already signed the renter in, and v3 sends alerts to the
    // session's account email.
    createSavedSearch.mutate(
      {
        postData: buildSavedSearchPayload({
          name: trimmedName,
          frequency,
          areaId: savedSearchContext.areaId,
          filters: draftFilters,
          areaBounds: savedSearchContext.areaBounds,
          searchScope: savedSearchContext.searchScope,
        }),
      },
      {
        onSuccess: () => {
          emitTrackCreateSearchAlertEvent({
            triggerLocation: 'post_contact_popup',
            triggerObject: 'post_contact_popup|alerts_modal',
          });
          onComplete();
        },
      },
    );
  };

  return (
    <div className={styles.stepBody} aria-busy={!hasResolvedIdentity}>
      <Text textStyle="body-sm" color="text.subtle">
        {t('postContactSavedSearchBody', {
          location: savedSearchContext.locationText,
        })}
      </Text>
      <div className={styles.fieldStack}>
        <FormField
          label={<Label>{saveSearchT('nameLabel')}</Label>}
          required
          error={!!nameError}
          description={nameError ? <FormHelp>{nameError}</FormHelp> : undefined}
          control={
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={savedSearchContext.locationText}
            />
          }
        />
        {shouldShowFilterTuning && <SavedSearchFilterTuning filters={draftFilters} onChange={setDraftFilters} />}
        <FormField
          label={<Label>{saveSearchT('frequencyLegend')}</Label>}
          control={
            <Select value={frequency} onChange={(e) => setFrequency(e.target.value as SavedSearchFrequency)}>
              {SAVED_SEARCH_FREQUENCY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {saveSearchT(SAVED_SEARCH_FREQUENCY_LABEL_KEYS[option])}
                </option>
              ))}
            </Select>
          }
        />
      </div>
      {mutationError && (
        <p className={styles.alertText} role="alert">
          {mutationError}
        </p>
      )}
      <div className={styles.actionRow}>
        <Button
          tone="neutral"
          emphasis="outlined"
          type="button"
          onClick={onComplete}
          disabled={createSavedSearch.isPending}
        >
          {t('skip')}
        </Button>
        <Button
          tone="brand"
          emphasis="filled"
          type="button"
          onClick={handleSubmit}
          disabled={!hasResolvedIdentity || createSavedSearch.isPending}
        >
          {createSavedSearch.isPending ? saveSearchT('saving') : t('postContactSavedSearchSubmit')}
        </Button>
      </div>
    </div>
  );
}

export function SavedSearchFlowStep({ context, onComplete }: PostContactFlowStepProps) {
  if (!context.savedSearchContext) {
    return null;
  }

  return <SavedSearchStep savedSearchContext={context.savedSearchContext} onComplete={onComplete} />;
}

export function WaitlistSuccessStep({ context, onComplete }: PostContactFlowStepProps) {
  const t = useTranslations('contactForm');
  const listings = context.similarListings.slice(0, 2);

  return (
    <div className={styles.stepBody}>
      <div className={styles.statusCopy} role="status" aria-live="polite">
        <Text textStyle="body-sm" color="text.subtle">
          {t('postContactWaitlistSuccessBody')}
        </Text>
        <Text textStyle="body-sm" color="text.subtle">
          {t('postContactWaitlistSuccessPrompt')}
        </Text>
      </div>
      <ListingCarousel aria-label={t('postContactSimilarListingsLabel')}>
        {listings.map((listing) => (
          <div key={getPostContactListingId(listing)} className={styles.listingCarouselItem}>
            <PostContactListingCard listing={listing} />
          </div>
        ))}
      </ListingCarousel>
      <div className={styles.actionRow}>
        <Button tone="brand" emphasis="filled" type="button" onClick={onComplete}>
          {t('done')}
        </Button>
      </div>
    </div>
  );
}

export function MultiContactStep({ context, onComplete }: PostContactFlowStepProps) {
  const t = useTranslations('contactForm');
  const mockInquiryEnabled = useFeatureFlag('mock-inquiry');
  const createInquiry = useCreateInquiry();
  const listings = context.similarListings;
  const [selectedIds, setSelectedIds] = useState<Array<string>>(() =>
    listings.map(getPostContactListingId).filter(Boolean),
  );
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedIds(listings.map(getPostContactListingId).filter(Boolean));
    setSent(false);
    setError(null);
  }, [listings]);

  const contactableListingIds = listings.map(getPostContactListingId).filter(Boolean);
  const allSelected = contactableListingIds.length > 0 && selectedIds.length === contactableListingIds.length;

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? contactableListingIds : []);
  };

  const handleListingToggle = (listingId: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? Array.from(new Set([...prev, listingId])) : prev.filter((id) => id !== listingId),
    );
  };

  const handleSend = async () => {
    setError(null);
    const selectedListings = listings.filter((listing) => selectedIds.includes(getPostContactListingId(listing)));
    emitTrackSimilarListingMultiContactMessageButtonClickEvent({
      listing: toHotpadsClickstreamListing(context.listing),
      ...getPostContactClickstreamContext(context.clickstreamContext),
    });

    try {
      if (mockInquiryEnabled) {
        selectedListings.forEach((listing) => {
          emitSuccessfulSimilarListingInquiryEvents(context, listing, createRentalSubmitId());
        });
        setSent(true);
        return;
      }

      for (const listing of selectedListings) {
        if (!listing.aliasEncoded) continue;
        const rentalSubmitId = createRentalSubmitId();
        // No email: v3 takes it from the session, which is live by now — this step only
        // renders after the originating inquiry succeeded.
        await createInquiry.mutateAsync({
          postData: {
            listingAlias: listing.aliasEncoded,
            phone: context.inquiry.phone,
            name: context.inquiry.name,
            text: t('postContactMultiContactDefaultMessage'),
            requestToApply: false,
            rentalSubmitId,
          },
        });
        emitSuccessfulSimilarListingInquiryEvents(context, listing, rentalSubmitId);
      }
      setSent(true);
    } catch {
      setError(t('postContactMultiContactSubmitError'));
    }
  };

  if (listings.length === 0) {
    return (
      <div className={styles.stepBody}>
        <p className={styles.statusText}>{t('postContactNoSimilarListings')}</p>
        <div className={styles.actionRow}>
          <Button tone="brand" emphasis="filled" onClick={onComplete}>
            {t('done')}
          </Button>
        </div>
      </div>
    );
  }

  const isSending = !mockInquiryEnabled && createInquiry.isPending;

  return (
    <div className={styles.stepBody}>
      <Text textStyle="body-sm" color="text.subtle">
        {t('postContactMultiContactBody')}
      </Text>
      <LabeledControl
        label={<Label>{t('postContactSelectAll')}</Label>}
        control={
          <Checkbox
            checked={allSelected}
            onChange={(e) => handleSelectAll(e.target.checked)}
            disabled={isSending || sent}
          />
        }
      />
      <ListingCarousel aria-label={t('postContactSimilarListingsLabel')}>
        {listings.map((listing) => {
          const listingId = getPostContactListingId(listing);

          return (
            <div key={listingId} className={styles.listingCarouselItem}>
              <PostContactListingCard
                listing={listing}
                isSelected={selectedIds.includes(listingId)}
                onSelectedChange={handleListingToggle}
                disabled={isSending || sent}
              />
            </div>
          );
        })}
      </ListingCarousel>
      <p className={styles.statusText}>{t('postContactPropertiesSelected', { count: selectedIds.length })}</p>
      {error && (
        <p className={styles.alertText} role="alert">
          {error}
        </p>
      )}
      <div className={styles.actionRow}>
        {sent ? (
          <Button tone="brand" emphasis="filled" onClick={onComplete}>
            {t('done')}
          </Button>
        ) : (
          <>
            <Button tone="neutral" emphasis="outlined" type="button" onClick={onComplete} disabled={isSending}>
              {t('skip')}
            </Button>
            <GatedButton
              tone="brand"
              emphasis="filled"
              type="button"
              onClick={handleSend}
              disabled={selectedIds.length === 0 || isSending}
              gateReason={!isSending && selectedIds.length === 0 && t('gateHintSelectListings')}
            >
              {isSending ? t('sending') : t('postContactSendMessages')}
            </GatedButton>
          </>
        )}
      </div>
      {sent && <p className={styles.statusText}>{t('postContactMessagesSent')}</p>}
    </div>
  );
}

export function SuccessStep({ context, onComplete }: PostContactFlowStepProps) {
  const t = useTranslations('contactForm');

  const title =
    context.mode === 'tour'
      ? t('successTitleTour')
      : context.isWaitlist
        ? t('successTitleWaitlist')
        : t('successTitleContact');

  return (
    <div className={styles.stepBody}>
      <div className={styles.statusCopy} role="status" aria-live="polite">
        <Text textStyle="body-sm" color="text.subtle">
          {title}
        </Text>
        <Text textStyle="body-sm" color="text.subtle">
          {t('successBody')}
        </Text>
      </div>
      <div className={styles.actionRow}>
        <Button tone="brand" emphasis="filled" type="button" onClick={onComplete}>
          {t('done')}
        </Button>
      </div>
    </div>
  );
}
